import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '../../assets/search.svg';
import { useGlobalContext } from "../../context/Appcontext";

interface SearchResult {
  sessionId: string;
  matchedMessage: string;
  firstMessage: string;
}

interface SearchPopupProps {
  open: boolean;
  onClose: () => void;
}

const SearchPopup: React.FC<SearchPopupProps> = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const { chats, setActiveChat } = useGlobalContext();

  const getMessageText = (message: any): string => {
    if (!message) return '';
    if (typeof message.message === 'string') return message.message;
    if (typeof message.text === 'string') return message.text;
    return '';
  };

  useEffect(() => {
    if (searchQuery) {
      try {
        const results: SearchResult[] = [];
        
        chats.forEach((chat: any) => {
          if (!Array.isArray(chat.messages)) return;
          
          // Search through all messages in the chat
          chat.messages.forEach((message: any) => {
            const messageText = getMessageText(message);
            if (messageText.toLowerCase().includes(searchQuery.toLowerCase())) {
              // Add this chat to results if not already added
              const existingResult = results.find(r => r.sessionId === chat.session_id);
              if (!existingResult) {
                results.push({
                  sessionId: chat.session_id,
                  matchedMessage: messageText,
                  firstMessage: getMessageText(chat.messages[0]) || "New Chat"
                });
              }
            }
          });
        });
        
        setSearchResults(results);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, chats]);

  const handleChatSelect = (sessionId: string) => {
    setActiveChat(sessionId);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: styles.dialogPaper
      }}
    >
      <DialogContent sx={styles.dialogContent}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={styles.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <img src={SearchIcon} alt="Search" />
              </InputAdornment>
            ),
          }}
        />
        
        <List sx={styles.resultsList}>
          {searchResults.length > 0 ? (
            searchResults.map((result) => (
              <ListItemButton
                key={result.sessionId}
                onClick={() => handleChatSelect(result.sessionId)}
                sx={styles.resultItem}
              >
                <ListItemText
                  primary={result.firstMessage}
                  secondary={
                    <Typography
                      component="div"
                      variant="body2"
                      sx={styles.matchedText}
                    >
                      {result.matchedMessage}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))
          ) : searchQuery ? (
            <Typography sx={styles.noResults}>
              No conversations found
            </Typography>
          ) : (
            <Typography sx={styles.placeholder}>
              Start typing to search your conversations
            </Typography>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  dialogPaper: {
    borderRadius: '20px',
    background: '#fff',
    width: '600px',
    maxHeight: '70vh',
  },
  dialogContent: {
    p: 3,
  },
  searchInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '100px',
      border:"none",
      backgroundColor: '#F5F5F5',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: '#76528B',
      },
      '&.Mui-focused fieldset': {
        border:"none",
      },
    },
  },
  resultsList: {
    mt: 2,
    maxHeight: '50vh',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#76528B',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
  },
  resultItem: {
    borderRadius: '12px',
    mb: 1,
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
  matchedText: {
    color: '#666',
    fontSize: '14px',
    mt: 0.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  noResults: {
    textAlign: 'center',
    color: '#666',
    mt: 2,
  },
  placeholder: {
    textAlign: 'center',
    color: '#666',
    mt: 2,
    fontStyle: 'italic',
  },
};

export default SearchPopup;